import { useCallback, memo } from 'react';
import { SettingsMenuItem } from './MenuItem';
import { TEAM_MENU_ITEMS, type MenuItem, type MenuConfig } from './constants';
import * as Tooltip from '~/components/ui/Tooltip';
import { useMediaQuery } from '~/lib/hooks/useMediaQuery';
import { SettingsPage } from '~/lib/stores/menu';
import type { OrganizationData } from '~/types/organization';
import { classNames } from '~/utils/classNames';

interface MenuSectionProps {
  config: MenuConfig;
  currentPage: SettingsPage;
  setCurrentPage: (id: SettingsPage) => void;
  className?: string;
  titleClassName?: string;
  activeOrganization?: OrganizationData | null;
}

export const BaseMenuSection = memo(
  ({ config, currentPage, setCurrentPage, className, activeOrganization, titleClassName }: MenuSectionProps) => (
    <section className={classNames('flex flex-col', className)}>
      <div
        className={classNames(
          'sticky top-0 bg-bolt-ds-surface z-layer-1 px-3 pt-1 pb-2 text-bolt-ds-textTertiary text-xs font-semibold hidden md:block',
          titleClassName,
        )}
      >
        {config.title}
      </div>
      <ul className="flex flex-col gap-1">
        {config.items.map((item) => (
          <li key={item.id}>
            <SettingTab menuItem={item} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </li>
        ))}
        {!activeOrganization && config.title === 'Personal' && (
          <SettingTab menuItem={TEAM_MENU_ITEMS.items[0]} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        )}
      </ul>
    </section>
  ),
);

BaseMenuSection.displayName = 'MenuSection';

interface SettingTabProps {
  menuItem: MenuItem;
  className?: string;
  currentPage?: SettingsPage;
  setCurrentPage: (id: SettingsPage) => void;
}

const SettingTab = memo(({ menuItem, currentPage, setCurrentPage, className }: SettingTabProps) => {
  const isMdBreakpoint = useMediaQuery('(min-width: 768px)');
  const { id, icon, text } = menuItem;
  const active = currentPage === id;

  const handleClick = useCallback(() => {
    setCurrentPage(id);
  }, [id, setCurrentPage]);

  return (
    <>
      {isMdBreakpoint ? (
        <SettingsMenuItem
          className={className}
          icon={icon}
          text={text}
          comingSoon={menuItem.isComingSoon}
          active={active}
          onClick={handleClick}
        />
      ) : (
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <SettingsMenuItem
                className={className}
                icon={icon}
                text={text}
                comingSoon={menuItem.isComingSoon}
                active={active}
                onClick={handleClick}
              />
            </Tooltip.Trigger>

            <Tooltip.Portal>
              <Tooltip.Content side="bottom" align="center" sideOffset={6}>
                {text}
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      )}
    </>
  );
});

SettingTab.displayName = 'SettingTab';
